/**
 * @module
 *
 * @author Jacob Viertel [jv@onscreen.net](mailto:jv@onscreen.net)
 *
 * @since 3.101.0
 */
import {Closest, get, HREF} from '@acng/frontend-bounty/dom/attribute.js';
import {ANCHOR, Matcher} from '@acng/frontend-bounty/dom/element.js';
import {CLICK, off, on} from '@acng/frontend-bounty/dom/event.js';
import {isHTMLElement} from '@acng/frontend-bounty/dom/type.js';
import {window} from '@acng/frontend-bounty/dom/window.js';
import {AttributeLink, AttributeSetup} from '@acng/frontend-stargazer';
import {guard, IS_ELEMENT} from '@acng/frontend-rubicon';
import {OUT_STATE} from 'acng/voyager/attribute/out-state';

import {ONCLICK_TARGET} from './onclick-target.js';

/**
 * Create a {@link CLICK | click} listener on the element that opens the
 * specified window location.
 *
 * - If the element is an {@link ANCHOR | `<anchor>`}, also set the
 *   {@link HREF | `[href]`} attribute.
 * - Respects an optional {@link ONCLICK_TARGET | target specifier}.
 * - If the attribute value is not set, use the closest
 *   {@link OUT_STATE | `[out-state]`} value.
 *
 * @example
 *
 * ```html
 * <span onclick-href="/">Home</span>
 * ```
 *
 * @group DOM Attribute
 */
const name = 'onclick-href';

AttributeSetup(name, () => {
  const getStateInput = Closest(OUT_STATE);

  AttributeLink(name, (element, value) => {
    const isAnchor = isHTMLElement(element, ANCHOR);
    const matchesTarget = Matcher(get(element, ONCLICK_TARGET));

    /**
     * @param {MouseEvent} evt
     */
    const click = (evt) => {
      ASSERT: guard(evt.target, IS_ELEMENT, 'click target');

      if (isAnchor) {
        evt.preventDefault();
      }

      if (matchesTarget(evt.target)) {
        window.location.hash = value;
      }
    };

    value ||= getStateInput(element) ?? '';

    if (isAnchor) {
      element[HREF] = `#${value}`;
    }

    return (el) => (on(el, CLICK, click), (el) => off(el, CLICK, click));
  });
});

export {name as ONCLICK_HREF};
